export const RETIRED_INTAKE_STORAGE = Object.freeze({
  database: 'chamber2026-protected-intake',
  store: 'capsule',
  evidenceMarker: 'retired-intake-purge:capsule',
});

export type RetiredIntakePurgeState = 'pending' | 'complete' | 'blocked' | 'error' | 'unsupported';

export type RetiredIntakePurgeStatus = Readonly<{
  state: RetiredIntakePurgeState;
  database: typeof RETIRED_INTAKE_STORAGE.database;
  store: typeof RETIRED_INTAKE_STORAGE.store;
  evidenceMarker: typeof RETIRED_INTAKE_STORAGE.evidenceMarker;
}>;

type DeleteFactory = Pick<IDBFactory, 'deleteDatabase'>;
type StatusRecorder = (status: RetiredIntakePurgeStatus) => void;

const statusFor = (state: RetiredIntakePurgeState): RetiredIntakePurgeStatus => ({
  state,
  ...RETIRED_INTAKE_STORAGE,
});

/**
 * Delete only the retired intake database. This never opens the database,
 * reads its contents, enumerates databases, or addresses another store/name.
 */
export function purgeRetiredIntakeStorage(
  factory: DeleteFactory | undefined,
  record: StatusRecorder = () => undefined,
): Promise<RetiredIntakePurgeStatus> {
  record(statusFor('pending'));
  if (!factory) {
    const status = statusFor('unsupported');
    record(status);
    return Promise.resolve(status);
  }

  return new Promise(resolve => {
    let firstOutcomeRecorded = false;
    const report = (state: Exclude<RetiredIntakePurgeState, 'pending'>) => {
      const status = statusFor(state);
      record(status);
      if (!firstOutcomeRecorded) {
        firstOutcomeRecorded = true;
        resolve(status);
      }
    };

    let request: IDBOpenDBRequest;
    try {
      request = factory.deleteDatabase(RETIRED_INTAKE_STORAGE.database);
    } catch {
      report('error');
      return;
    }
    request.onsuccess = () => report('complete');
    request.onerror = () => report('error');
    request.onblocked = () => report('blocked');
  });
}

let currentStatus = statusFor('pending');
let startupPurge: Promise<RetiredIntakePurgeStatus> | undefined;
const listeners = new Set<() => void>();

const recordStartupStatus: StatusRecorder = status => {
  currentStatus = status;
  for (const listener of listeners) listener();
  if (status.state === 'blocked' || status.state === 'error' || status.state === 'unsupported') {
    console.warn(`[retired-intake-purge] ${status.state}; ${status.evidenceMarker}`);
  }
};

export function startRetiredIntakePurge(): Promise<RetiredIntakePurgeStatus> {
  startupPurge ??= purgeRetiredIntakeStorage(globalThis.indexedDB, recordStartupStatus);
  return startupPurge;
}

export function getRetiredIntakePurgeStatus(): RetiredIntakePurgeStatus {
  return currentStatus;
}

export function subscribeRetiredIntakePurge(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
